import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import { DrbState, DrbStateModel, LoadDrbByVersion, LoadDrbVersions } from 'src/app/features/drbs/redux';
import { ActionButton, ActionDisplay, Content, DestinationType, Fields, PayloadRecord } from 'src/app/models/content';
import { Drb } from 'src/app/models/drb-models/drb';
import {
  ACTION_TYPE,
  ACTIONS,
  ACTIVE_PARENT_URI_ID,
  BUNDLE_NAMES,
  DISPLAY_TYPE,
  MESSAGE_TYPE,
  NOTIFICATION_ICONS,
  PAYLOAD_TYPES,
  PRIME_MIN_APP_VERSIONS,
  PRIME_URIS
} from 'src/app/shared/constants/general-constants';
import { SharedModule } from 'src/app/shared/shared.module';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { PreviewContentComponent } from 'src/app/shared/ui/preview-content/preview-content.component';
import { ENVIRONMENT } from 'src/environments/environment';
import { RouterState } from '../../../auth/redux';
import { DrbService } from '../../../drbs/services/drb.service';
import { PostContent, PostSaveComplete, UpdateContentSuccess } from '../../redux';
import { UpdateContent } from '../../redux/content.actions';

@Component({
  selector: 'app-edit-content',
  templateUrl: './edit-content.component.html',
  styleUrls: ['./edit-content.component.css'],
  standalone: true,
  imports: [SharedModule]
})
export class EditContentComponent implements OnInit, OnDestroy {
  @ViewChild('contentForm', { static: false }) public contentForm?: NgForm;
  public version$: Observable<Record<string, string[]>> = inject(Store).select(DrbState.versions);
  public marketing$: Observable<Drb | undefined> = inject(Store).select(DrbState.marketing);
  public marketing_translations$: Observable<Drb | undefined> = inject(Store).select(DrbState.marketing_translations);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public content!: Content;
  public action!: string;
  public drbIds: Record<string, string[]> = {};
  public marketing?: Drb = undefined;
  public marketing_translations?: Drb = undefined;
  public disableRefreshButton = false;
  public action_button_enabled = false;

  public invalidContent = false;
  public validationMessage = '';
  public prime = false;

  public actionDisplays = ActionDisplay;
  public destinationTypes = DestinationType;
  public fields = Fields;
  public CategoryTitles = [
    {
      category: NOTIFICATION_ICONS.SPECIAL_OFFER,
      id: 'just_for_you_category_title_id_dmc',
      text: $localize`Just for you`
    },
    {
      category: NOTIFICATION_ICONS.NEW_PRODUCT,
      id: 'iseries_upsell_category_title_id_dmc',
      text: $localize`New Product`
    },
    { category: NOTIFICATION_ICONS.TIPS_NORMAL, id: 'tips_dmc', text: $localize`Tips` },
    { category: NOTIFICATION_ICONS.MAPS_DMC, id: 'map_suggestion_dmc', text: $localize`Map Suggestion` },
    { category: NOTIFICATION_ICONS.MAPS_DMC, id: 'map_update_dmc', text: $localize`Map Update` },
    { category: NOTIFICATION_ICONS.TIPS, id: 'tips_dmc', text: $localize`Tips` },
    { category: NOTIFICATION_ICONS.MAINTENANCE, id: 'to_dos_dmc', text: $localize`Maintenance` },
    { category: NOTIFICATION_ICONS.FEEDBACK, id: 'feedback_dmc', text: $localize`Feedback` },
    { category: 'all', id: 'special_offer_category_title_id_dmc', text: $localize`Special Offers` }
  ];

  public categoryIconId = [
    NOTIFICATION_ICONS.SPECIAL_OFFER,
    NOTIFICATION_ICONS.NEW_PRODUCT,
    NOTIFICATION_ICONS.TIPS_NORMAL,
    NOTIFICATION_ICONS.MAPS_DMC,
    NOTIFICATION_ICONS.TIPS,
    NOTIFICATION_ICONS.MAINTENANCE,
    NOTIFICATION_ICONS.FEEDBACK,
    NOTIFICATION_ICONS.BEYOND_CLEANING
  ];
  public actionType = ACTION_TYPE;
  public isScriptedNavigationEnabled = false;
  public displayType = DISPLAY_TYPE;
  public versions: string[] = [];

  public messageType = MESSAGE_TYPE;
  public message_type: string[] = [];

  public get availableMessageTypes() {
    const hasMultiPage = this.message_type.includes(PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
    const hasOtherType = this.message_type.some((type) => type !== PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);

    return this.messageType.filter((type) => {
      if (type === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG) {
        return this.prime && !hasOtherType;
      }
      return !hasMultiPage;
    });
  }

  public get payloadRecords(): PayloadRecord[] {
    if (!this.content.payload_records) {
      this.content.payload_records = [];
    }

    return this.content.payload_records;
  }

  public legacyUris = ACTIVE_PARENT_URI_ID;
  public primeUris = PRIME_URIS;

  public username = '';

  constructor(
    protected ref: NbDialogRef<EditContentComponent>,
    private store: Store,
    private dialogService: NbDialogService,
    private action$: Actions,
    private drbService: DrbService,
    private toastrService: NbToastrService,
    private utilityService: UtilityService
  ) {
    ENVIRONMENT.bundleNames.forEach((bundle_name) => {
      if (this.store.selectSnapshot(DrbState.versions)[bundle_name].length === 0) {
        this.store.dispatch(new LoadDrbVersions(bundle_name));
      }
    });
  }

  public ngOnInit() {
    if (this.content.action_button && this.content.action_button.length > 0) {
      this.action_button_enabled = true;

      const helperArray = [];
      const clonedActionButton: ActionButton = new ActionButton();
      Object.assign(clonedActionButton, this.content.action_button[0]);
      helperArray.push(clonedActionButton);
      this.content.action_button = helperArray;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    this.content = <Content>Content.clone(this.content);
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.prime = this.utilityService.isPrimeMinAppVersion(this.content.min_app_version);

    this.generateVersions();

    if (this.action === ACTIONS.EDIT) {
      if (this.content.payload_records) {
        const array: PayloadRecord[] = [];
        this.content.payload_records.forEach((element) => {
          const objClone = { ...element };
          if (objClone.payload_record_type === PAYLOAD_TYPES.MSG_CENTER) {
            this.message_type.push(PAYLOAD_TYPES.MSG_CENTER);
          }
          if (objClone.payload_record_type === PAYLOAD_TYPES.CONTEXTUAL_MSG) {
            this.message_type.push(PAYLOAD_TYPES.CONTEXTUAL_MSG);
          }
          if (
            objClone.payload_record_type === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG &&
            !this.message_type.includes(PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG)
          ) {
            this.message_type.push(PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
          }
          if (objClone.payload_record_type === PAYLOAD_TYPES.BANNER) {
            this.message_type.push(PAYLOAD_TYPES.BANNER);
          }
          if (objClone.payload_record_type === PAYLOAD_TYPES.PERSONALIZED_OFFER) {
            this.message_type.push(PAYLOAD_TYPES.PERSONALIZED_OFFER);
          }
          objClone.content_html = this.htmlDecode(objClone.content_html);
          array.push(objClone);
        });
        this.content.payload_records = array;
      }
      this.action$.pipe(ofActionCompleted(UpdateContentSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
    } else if (this.action === ACTIONS.CREATE) {
      this.content.payload_records = [];
      this.action$.pipe(ofActionCompleted(PostSaveComplete), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
    } else if (this.action === ACTIONS.CLONE) {
      if (this.content.payload_records) {
        const array: PayloadRecord[] = [];
        this.content.payload_records.forEach((element) => {
          const objClone = { ...element };
          if (objClone.payload_record_type === PAYLOAD_TYPES.MSG_CENTER) {
            this.message_type.push(PAYLOAD_TYPES.MSG_CENTER);
          }
          if (objClone.payload_record_type === PAYLOAD_TYPES.CONTEXTUAL_MSG) {
            this.message_type.push(PAYLOAD_TYPES.CONTEXTUAL_MSG);
          }
          if (
            objClone.payload_record_type === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG &&
            !this.message_type.includes(PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG)
          ) {
            this.message_type.push(PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
          }
          if (objClone.payload_record_type === PAYLOAD_TYPES.BANNER) {
            this.message_type.push(PAYLOAD_TYPES.BANNER);
          }
          if (objClone.payload_record_type === PAYLOAD_TYPES.PERSONALIZED_OFFER) {
            this.message_type.push(PAYLOAD_TYPES.PERSONALIZED_OFFER);
          }
          objClone.content_html = this.htmlDecode(objClone.content_html);
          array.push(objClone);
        });
        this.content.payload_records = array;
      }
      this.action$.pipe(ofActionCompleted(PostSaveComplete), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
    }

    if (this.content && this.content.action_type === 'scripted_navigation') {
      this.isScriptedNavigationEnabled = true;
    }
    this.drbIds = this.drbService.getDrbIds();
    this.marketing$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.marketing = data));
    this.marketing_translations$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => (this.marketing_translations = data));
    this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      ENVIRONMENT.bundleNames.forEach((bundle_name) => {
        if (data[bundle_name].length > 0) {
          const versions = [...data[bundle_name]].reverse();
          if (!this.store.selectSnapshot(DrbState.getByKey(bundle_name as keyof DrbStateModel))) {
            this.store.dispatch(new LoadDrbByVersion(versions[0], bundle_name));
          }
        }
      });
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public togglePrime(minAppVer: any) {
    this.prime = this.utilityService.isPrimeMinAppVersion(minAppVer);
    this.content.active_parent_uri_id = undefined;

    if (!this.prime) {
      this.content.payload_records = this.payloadRecords.filter(
        (r) => r.payload_record_type !== PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG
      );
      this.message_type = this.message_type.filter((type) => type !== PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
    }
  }

  public generateVersions() {
    const min = 500;
    const max = 700;
    let current = min;
    for (const v of PRIME_MIN_APP_VERSIONS) {
      this.versions.push(v);
    }

    // Legacy App
    const minVersion = min.toString().match(/.{1}/g);
    if (minVersion !== null) {
      this.versions.push(minVersion.join('.'));
    }

    while (current < max) {
      current += 10;
      const current_version = current.toString().match(/.{1}/g);
      if (current_version) {
        this.versions.push(current_version.join('.'));
      }
    }
  }

  public groupByFn = (version: string) =>
    this.utilityService.isPrimeMinAppVersion(version) ? $localize`Prime App` : $localize`Legacy App`;

  public close() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Confirm Close`,
          message: $localize`Are you sure that you want to close the window? Any changes that you have made will be lost.`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
  }

  public validate(id: string) {
    if (!this.contentForm || !this.contentForm.form.get(id) || !this.contentForm.form.get(id)?.hasError) {
      return null;
    }

    return this.contentForm.form.get(id);
  }

  public previewContent() {
    this.dialogService.open(PreviewContentComponent, {
      closeOnEsc: true,
      closeOnBackdropClick: true,
      context: { content: this.content }
    });
  }

  public save() {
    this.payloadRecords.forEach((element: PayloadRecord) => {
      if (element.content_html) {
        element.content_html = this.escapeHtml(element.content_html);
      }

      for (const property of this.fields) {
        const typedProperty = property as keyof PayloadRecord;
        if (element[typedProperty] !== undefined && element[typedProperty] !== null) {
          if (element[typedProperty].toString().length === 0) {
            delete element[typedProperty];
          }
        }
      }
    });
    if (this.action === ACTIONS.CLONE) {
      delete this.content.content_id;
      delete this.content.content_version;
      delete this.content.content_key;
    }
    if (this.action === ACTIONS.EDIT) {
      this.content.last_updated_by = this.username;
      this.store.dispatch(new UpdateContent(this.content));
    } else {
      this.content.created_by = this.username;
      this.content.last_updated_by = this.username;
      this.store.dispatch(new PostContent(this.content));
    }
  }

  public escapeHtml(unsafe: string) {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  public htmlDecode(input: string | undefined) {
    if (input !== undefined) {
      const e = document.createElement('div');
      e.innerHTML = input;

      if (e.childNodes.length === 0) {
        return '';
      }
      if (e.childNodes[0].nodeValue) {
        return e.childNodes[0].nodeValue;
      }
    }
    return undefined;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public onSelectionChange(event: string) {
    switch (event) {
      case NOTIFICATION_ICONS.SPECIAL_OFFER:
        this.content.category_title_id = 'special_offer_category_title_id_dmc';
        this.content.category_title_text = $localize`Special Offers`;

        return true;
      case NOTIFICATION_ICONS.NEW_PRODUCT:
        this.content.category_title_id = 'iseries_upsell_category_title_id_dmc';
        this.content.category_title_text = $localize`New Product`;

        return true;
      case NOTIFICATION_ICONS.TIPS_NORMAL:
        this.content.category_title_id = 'tips_dmc';
        this.content.category_title_text = $localize`Tips`;

        return true;
      case NOTIFICATION_ICONS.MAINTENANCE:
        this.content.category_title_id = 'to_dos_dmc';
        this.content.category_title_text = $localize`To Dos`;

        return true;
      case NOTIFICATION_ICONS.TIPS:
        this.content.category_title_id = 'tips';
        this.content.category_title_text = $localize`Tips`;

        return true;
      case NOTIFICATION_ICONS.MAPS_DMC:
        this.content.category_title_id = 'map_suggestion_dmc';
        this.content.category_title_text = $localize`Map Suggestion`;

        return true;
      case NOTIFICATION_ICONS.FEEDBACK:
        this.content.category_title_id = 'feedback_dmc';
        this.content.category_title_text = $localize`Feedback`;

        return true;
      default:
        return false;
    }
  }

  public onCategoryTitleSelect(event: string) {
    this.CategoryTitles.forEach((category) => {
      if (category.text === event) {
        this.content.category_title_id = category.id;
      }
    });
  }

  public actionButtonEvent(event: any) {
    if (event) {
      this.action_button_enabled = true;
      this.content.action_button = [];
      this.content.action_button.push(new ActionButton());
    } else {
      this.content.action_button = [];
      this.action_button_enabled = false;
    }
  }

  public shouldButtonEnabled() {
    if (this.content.payload_records) {
      const requiresUri = this.payloadRecords.some(
        (item) =>
          item.payload_record_type === PAYLOAD_TYPES.CONTEXTUAL_MSG ||
          item.payload_record_type === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG
      );
      if (requiresUri) {
        return !!this.content.active_parent_uri_id;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  public messageTypeAdd(event: string) {
    if (this.content.payload_records === null || undefined) {
      this.content.payload_records = [];
    }
    if (
      event !== PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG &&
      this.payloadRecords.some((e) => e.payload_record_type === event)
    ) {
      return;
    }
    if (event === PAYLOAD_TYPES.MSG_CENTER) {
      const payload_record = new PayloadRecord();
      payload_record.payload_record_type = PAYLOAD_TYPES.MSG_CENTER;
      payload_record.action_display = PAYLOAD_TYPES.TEMPLATE;
      this.payloadRecords.push(payload_record);
    }
    if (event === PAYLOAD_TYPES.CONTEXTUAL_MSG) {
      const payload_record = new PayloadRecord();
      payload_record.payload_record_type = PAYLOAD_TYPES.CONTEXTUAL_MSG;
      payload_record.action_display = PAYLOAD_TYPES.HALF_MODAL;
      this.payloadRecords.push(payload_record);
    }
    if (event === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG) {
      // Multi Page Contextual Message is exclusive: drop any other selected types/records so it
      // becomes the only message type.
      this.content.payload_records = [];
      this.message_type = [PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG];
      for (let page = 0; page < 2; page++) {
        const payload_record = new PayloadRecord();
        payload_record.payload_record_type = PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG;
        payload_record.action_display = PAYLOAD_TYPES.HALF_MODAL;
        this.payloadRecords.push(payload_record);
      }

      return;
    }
    if (event === 'banner') {
      const payload_record = new PayloadRecord();
      payload_record.payload_record_type = PAYLOAD_TYPES.BANNER;
      this.payloadRecords.push(payload_record);
    }
    if (event === PAYLOAD_TYPES.PERSONALIZED_OFFER) {
      const payload_record = new PayloadRecord();
      payload_record.payload_record_type = PAYLOAD_TYPES.PERSONALIZED_OFFER;
      payload_record.action_display = PAYLOAD_TYPES.PERSONALIZED_TEMPLATE;
      this.payloadRecords.push(payload_record);
    }
  }

  public messageTypeRemove(event: string) {
    this.content.payload_records = this.payloadRecords.filter((e) => e.payload_record_type !== event);
    if (event === PAYLOAD_TYPES.CONTEXTUAL_MSG || event === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG) {
      this.content.active_parent_uri_id = undefined;
    }
  }

  public activeMultiPageTabIdx = 0;

  public get multiPageRecordsWithIndex(): { index: number; payload: PayloadRecord }[] {
    return this.payloadRecords
      .map((payload, index) => ({ payload, index }))
      .filter(({ payload }) => payload.payload_record_type === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
  }

  public hasMultiPageRecords(): boolean {
    return this.payloadRecords.some((r) => r.payload_record_type === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
  }

  public onMultiPageTabChange(tab: any): void {
    const match = (tab.tabTitle as string)?.match(/Page (\d+)/);
    if (match) {
      this.activeMultiPageTabIdx = parseInt(match[1], 10) - 1;
    }
  }

  public addMultiPageRecord(): void {
    const payload_record = new PayloadRecord();
    payload_record.payload_record_type = PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG;
    payload_record.action_display = PAYLOAD_TYPES.HALF_MODAL;
    this.payloadRecords.push(payload_record);
  }

  public removeMultiPageRecord(): void {
    this.removeMultiPageRecordByTabIdx(this.activeMultiPageTabIdx);
  }

  public removeMultiPageRecordByTabIdx(tabIdx: number): void {
    const records = this.multiPageRecordsWithIndex;
    if (records.length <= 2) {
      return;
    }
    const target = records[tabIdx];
    if (target === undefined) {
      return;
    }
    this.payloadRecords.splice(target.index, 1);
    if (this.activeMultiPageTabIdx >= records.length - 1) {
      this.activeMultiPageTabIdx = records.length - 2;
    }
  }

  public messageTypeClear() {
    this.content.payload_records = [];
  }

  public keyPressNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    // Only Numbers 0-9
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();

      return false;
    } else {
      return true;
    }
  }

  public availableCategories() {
    return this.CategoryTitles.filter(
      (category) => category.category === this.content.category_icon_id || category.category === 'all'
    );
  }

  public onActionTypeChange() {
    this.isScriptedNavigationEnabled = this.content.action_type === 'scripted_navigation';

    if (!this.isScriptedNavigationEnabled && this.payloadRecords.length) {
      this.payloadRecords.forEach((record) => {
        record.script_id = undefined;
        record.script_id_display_order = undefined;
      });
    }
  }

  public addTag(tag: string) {
    /* https://github.com/ng-select/ng-select/issues/809 */
    return tag;
  }

  public refresh() {
    this.disableRefreshButton = true;
    for (const key in BUNDLE_NAMES) {
      const bundle_name = key.toLowerCase();
      this.drbService.loadDrbVersions(bundle_name);
      setTimeout(() => {
        const versions = [...this.store.selectSnapshot(DrbState.versions)[bundle_name]].reverse();
        this.drbService.loadDrbByVersion(versions[0], bundle_name);
      }, 2500);
    }
    setTimeout(() => {
      this.drbIds = this.drbService.getDrbIds();
      this.toastrService.show('', $localize`DRB IDs refreshed successfully`, { status: 'success' });
      this.disableRefreshButton = false;
    }, 5000);
  }
}
